import { useEffect, useState } from "react";
import {
  actionKey,
  SEVERITY_TONE,
  CASE_SEVERITY_TONE,
  REVOKE_FILE_ACCESS,
  DECK_PAGE_SIZE,
  TABLE_PAGE_SIZES,
  CLUSTER_FETCH_SIZE,
  ensureCategoryLoaded,
} from "../DataControlConstans";
import {
  Badge,
  formatTypeLabel,
  IconOrInitials,
  parseTypeList,
} from "../DataControlUtils";
import { StatCard } from "./DataControlCarouselCard";
import { useSelector, useDispatch } from "react-redux";
import Page_Loader from "../../../assets/vizr_images/page_loader.gif";
import { DataGrid, GridOverlay } from "@mui/x-data-grid";
import { renderDateTime } from "../../../components/views/Util/ViewCommon";
import { isNull } from "lodash";
import { icons } from "../../../components/common/utilities";
import { categoryIcons } from "../../Data Tag/DataEstate/DataEstateDrives";
import DataControlSensitivityScatter from "./DataControlSensitivityScatter";

function getSourceIcon(dataSourceType) {
  return icons[(dataSourceType || "").toUpperCase()] || icons.OTHER;
}

function getBulkActionLabel(categoryId) {
  if (categoryId === "location") return "Change Status";
  if (categoryId === "user-risk") return "Remove Public link";
  if (categoryId === "org-wide") return "Remove Org-wide Public Links";
  return "Remove public access";
}

function daysAgo(dateStr) {
  if (!dateStr) return null;
  const diffMs = Date.now() - new Date(dateStr).getTime();
  return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
}

function getMajorityTone(items) {
  const counts = {};
  items.forEach((it) => {
    const tone = SEVERITY_TONE[it.severity] || "blue";
    counts[tone] = (counts[tone] || 0) + 1;
  });
  const order = ["red", "amber", "blue"];
  let best = "blue";
  let bestCount = -1;
  order.forEach((tone) => {
    const c = counts[tone] || 0;
    if (c > bestCount) {
      bestCount = c;
      best = tone;
    }
  });
  return best;
}

export function RiskItemDeck({
  categoryId,
  items,
  loadedCount,
  totalCount,
  appliedActions,
  onAction,
  dispatch,
  selectedIds,
  onToggleSelect,
  loading,
  canUpdate,
}) {
  const [page, setPage] = useState(0);
  const [direction, setDirection] = useState(1);
  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / DECK_PAGE_SIZE));

  useEffect(() => {
    setPage(0);
  }, [categoryId]);

  useEffect(() => {
    if (page > totalPages - 1) setPage(totalPages - 1);
  }, [totalPages, page]);

  const rangeEnd = (page + 1) * DECK_PAGE_SIZE;

  useEffect(() => {
    ensureCategoryLoaded(
      dispatch,
      categoryId,
      rangeEnd,
      loadedCount,
      totalCount,
    );
  }, [rangeEnd, categoryId, loadedCount, totalCount]);

  const pageItems = items.slice(page * DECK_PAGE_SIZE, rangeEnd);
  const isPageLoading = loading && rangeEnd > loadedCount;
  const activeTone = getMajorityTone(pageItems);

  const goPrev = () => {
    setDirection(-1);
    setPage((p) => Math.max(0, p - 1));
  };
  const goNext = () => {
    setDirection(1);
    setPage((p) => Math.min(totalPages - 1, p + 1));
  };
  const goToPage = (i) => {
    setDirection(i > page ? 1 : -1);
    setPage(i);
  };

  if (isPageLoading && pageItems.length === 0) {
    return (
      <div className="fsc-item-deck">
        <div className="fsc-fullscreen__items fsc-fullscreen__items--row">
          {Array.from({ length: DECK_PAGE_SIZE }).map((_, i) => (
            <div
              key={i}
              className="fsc-risk-item fsc-risk-item--card fsc-risk-item--skeleton"
            />
          ))}
        </div>
        <div className="fsc-item-deck__nav">
          <button
            className="fsc-item-deck__arrow"
            onClick={goPrev}
            disabled={page === 0 || loading}
            aria-label="Previous page"
          >
            <i className="ti ti-chevron-left" />
          </button>

          <div className="fsc-item-deck__dots">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                className={`fsc-item-deck__dot ${
                  i === page
                    ? `fsc-item-deck__dot--active fsc-item-deck__dot--${activeTone}`
                    : ""
                }`}
                onClick={() => goToPage(i)}
                aria-label={`Go to page ${i + 1}`}
              />
            ))}
          </div>

          <button
            className="fsc-item-deck__arrow"
            onClick={goNext}
            disabled={page === totalPages - 1 || loading}
            aria-label="Next page"
          >
            <i className="ti ti-chevron-right" />
          </button>
        </div>
      </div>
    );
  }

  if (!isPageLoading && pageItems.length === 0 && (totalCount || 0) === 0) {
    return (
      <p className="fsc-fullscreen__no-log">
        No flagged items found for this category.
      </p>
    );
  }

  return (
    <div className="fsc-item-deck">
      <div
        key={page}
        className={`fsc-fullscreen__items fsc-fullscreen__items--row ${
          direction === 1
            ? "fsc-item-deck__page--from-right"
            : "fsc-item-deck__page--from-left"
        }`}
      >
        {pageItems.map((item, i) => (
          <RiskItemRow
            key={item.id || i}
            item={item}
            dispatch={dispatch}
            categoryId={categoryId}
            appliedActions={appliedActions}
            onAction={onAction}
            selected={selectedIds.has(item.id)}
            onToggleSelect={onToggleSelect}
            canUpdate={canUpdate}
          />
        ))}
      </div>

      {totalPages > 1 && (
        <div className="fsc-item-deck__nav">
          <button
            className="fsc-item-deck__arrow"
            onClick={goPrev}
            disabled={page === 0 || loading}
            aria-label="Previous page"
          >
            <i className="ti ti-chevron-left" />
          </button>

          <div className="fsc-item-deck__dots">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                className={`fsc-item-deck__dot ${
                  i === page
                    ? `fsc-item-deck__dot--active fsc-item-deck__dot--${activeTone}`
                    : ""
                }`}
                onClick={() => goToPage(i)}
                aria-label={`Go to page ${i + 1}`}
              />
            ))}
          </div>

          <button
            className="fsc-item-deck__arrow"
            onClick={goNext}
            disabled={page === totalPages - 1 || loading}
            aria-label="Next page"
          >
            <i className="ti ti-chevron-right" />
          </button>
        </div>
      )}
    </div>
  );
}

export function RiskItemsTable({
  categoryId,
  items,
  loadedCount,
  totalCount,
  appliedActions,
  onAction,
  dispatch,
  loading,
  selectedIds,
  onSelectionModelChange,
  isUserRisk,
  canUpdate,
}) {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: TABLE_PAGE_SIZES[0],
  });

  useEffect(() => {
    setPaginationModel((m) => ({ ...m, page: 0 }));
  }, [categoryId]);

  const rangeEnd = (paginationModel.page + 1) * paginationModel.pageSize;

  useEffect(() => {
    ensureCategoryLoaded(
      dispatch,
      categoryId,
      rangeEnd,
      loadedCount,
      totalCount,
    );
  }, [rangeEnd, categoryId, loadedCount, totalCount]);

  const rows = items.slice(
    paginationModel.page * paginationModel.pageSize,
    rangeEnd,
  );
  const isTableLoading = loading && rangeEnd > loadedCount;

  const getButtonTitle = () => getBulkActionLabel(categoryId);

  const columns = [
    {
      field: "title",
      headerName: isUserRisk ? "User Name" : "File Name",
      flex: 1,
      renderCell: ({ row }) => (
        <div className="fsc-table__cell-vcenter">
          <div className="fsc-table__cell-main fsc-table__cell-main--with-icon title">
            <span>{row.title}</span>
            <img
              src={getSourceIcon(row.dataSourceType)}
              alt={row.dataSourceType}
              className="fsc-table__source-icon"
            />
          </div>
        </div>
      ),
    },
    {
      field: "severity",
      headerName: "Severity",
      width: 110,
      renderCell: ({ value }) => (
        <div className="fsc-table__cell-vcenter">
          <span
            className={`fsc-risk-card__priority fsc-risk-card__priority--${
              CASE_SEVERITY_TONE[value] || "blue"
            }`}
          >
            {value}
          </span>
        </div>
      ),
    },
    {
      field: "description",
      headerName: "Reason",
      flex: 2.4,
      renderCell: ({ row }) => (
        <div className="fsc-table__cell-vcenter">
          <span
            className="fsc-table__cell--reason"
            title={row.aiSummary || row.description}
          >
            {row.aiSummary || row.description}
          </span>
        </div>
      ),
    },

    ...(!isUserRisk
      ? [
          {
            field: "fileType",
            headerName: "File Type",
            flex: 1.3,
            renderCell: ({ row }) => {
              const types = parseTypeList(row.fileType);
              const visibleTypes = types.slice(0, 2);
              const extraCount = types.length - visibleTypes.length;

              if (types.length === 0) {
                return (
                  <div className="fsc-table__cell-vcenter">
                    <span className="fsc-table__cell--empty">—</span>
                  </div>
                );
              }

              return (
                <div className="fsc-table__cell-vcenter">
                  <div className="fsc-risk-item__reg-row">
                    {visibleTypes.map((t) => (
                      <span
                        key={t}
                        className="fsc-table__filetype-icon"
                        title={formatTypeLabel(t)}
                      >
                        <IconOrInitials
                          src={categoryIcons[t]}
                          label={formatTypeLabel(t)}
                          size={16}
                        />
                      </span>
                    ))}
                    {extraCount > 0 && (
                      <span
                        className="fsc-risk-item__reg-pill fsc-risk-item__reg-pill--more"
                        title={types.slice(2).map(formatTypeLabel).join(", ")}
                      >
                        +{extraCount}
                      </span>
                    )}
                  </div>
                </div>
              );
            },
          },
          {
            field: "regulations",
            headerName: "Regulations",
            flex: 1.2,
            sortable: false,
            renderCell: ({ row }) => {
              const regulations = row.regulations || [];

              if (regulations.length === 0) {
                return (
                  <div className="fsc-table__cell-vcenter">
                    <span className="fsc-table__cell--empty">—</span>
                  </div>
                );
              }

              const visibleRegs = regulations.slice(0, 2);
              const extraRegsCount = regulations.length - visibleRegs.length;

              return (
                <div className="fsc-table__cell-vcenter">
                  <div className="fsc-risk-item__reg-row">
                    {visibleRegs.map((reg) => (
                      <span key={reg} className="fsc-risk-item__reg-pill">
                        {reg}
                      </span>
                    ))}

                    {extraRegsCount > 0 && (
                      <span className="fsc-risk-item__reg-pill fsc-risk-item__reg-pill--more">
                        +{extraRegsCount}
                      </span>
                    )}
                  </div>
                </div>
              );
            },
          },
        ]
      : []),

    {
      field: "action",
      headerName: "Action",
      width: 200,
      sortable: false,
      renderCell: ({ row }) => {
        const applied = appliedActions[actionKey(categoryId, row.id)];

        return (
          <div className="fsc-table__cell-vcenter">
            {applied ? (
              <span className="fsc-risk-item__applied">
                <i className="ti ti-circle-check-filled" />
                {applied} applied
              </span>
            ) : (
              <button
                className="fsc-btn fsc-btn--ghost"
                onClick={() => onAction(row, getButtonTitle(), dispatch)}
                disabled={!canUpdate}
                title={
                  !canUpdate
                    ? "You don't have permission to perform this action"
                    : undefined
                }
              >
                {getButtonTitle()}
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ height: 460, width: "100%" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.fileId || row.id}
        loading={isTableLoading && rows.length === 0}
        pagination
        paginationMode="server"
        rowCount={totalCount || 0}
        pageSizeOptions={TABLE_PAGE_SIZES}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        checkboxSelection
        rowSelectionModel={Array.from(selectedIds)}
        onRowSelectionModelChange={(model) => onSelectionModelChange(model)}
        disableRowSelectionOnClick
        disableColumnMenu
        rowHeight={88}
        columnHeaderHeight={48}
        slots={{
          noRowsOverlay: () => (
            <GridOverlay>No flagged items found</GridOverlay>
          ),
        }}
        sx={{
          "& .MuiDataGrid-cellCheckbox": {
            display: "flex",
            alignItems: "center !important",
          },
        }}
      />
    </div>
  );
}

export function RiskItemRow({
  item,
  categoryId,
  appliedActions,
  onAction,
  dispatch,
  selected = false,
  onToggleSelect,
  canUpdate,
}) {
  const applied = appliedActions[actionKey(categoryId, item.id)];
  const tone = CASE_SEVERITY_TONE[item.severity] || "blue";
  const exposedDays = daysAgo(item.detectedOn);
  const sourceIcon = getSourceIcon(item.dataSourceType);

  const getButtonTitle = () => [getBulkActionLabel(categoryId)];

  const regulations = item.regulations || [];
  const visibleRegs = regulations.slice(0, 2);
  const extraRegsCount = regulations.length - visibleRegs.length;

  const iconTags = [];
  parseTypeList(item.fileType).forEach((t) => iconTags.push(t));

  return (
    <div
      className={`fsc-risk-item fsc-risk-item--card fsc-risk-item--${tone} ${
        selected ? "fsc-risk-item--selected" : ""
      }`}
      onClick={() => onToggleSelect && onToggleSelect(item.id)}
    >
      {selected && (
        <span className="fsc-risk-item__tick">
          <i className="ti ti-check" />
        </span>
      )}

      <div className="fsc-risk-item__header">
        <div className="fsc-risk-item__header-text">
          <p className="fsc-risk-item__subject" title={item.title}>
            <span className="fsc-risk-item__subject-text">{item.title}</span>
            <img
              src={sourceIcon}
              alt={item.dataSourceType}
              className={`fsc-source-logo ${
                (item.dataSourceType || "").toLowerCase() === "onedrive"
                  ? "fsc-source-logo--onedrive"
                  : ""
              }`}
            />
          </p>
          <p className="fsc-risk-item__owner">{item.subtitle}</p>
        </div>
      </div>

      {item.aiSummary && (
        <p
          className={`fsc-risk-item__ai-summary fsc-risk-item__ai-summary--${tone}`}
        >
          {item.aiSummary}
        </p>
      )}

      <div className="fsc-risk-item__meta-row">
        {exposedDays !== null && (
          <span>
            <i className="ti ti-clock" /> Exposed{" "}
            {exposedDays === 0 ? "today" : `${exposedDays}d`}
          </span>
        )}
        <span
          className={`fsc-risk-card__priority fsc-risk-card__priority--${tone}`}
        >
          {item.severity}
        </span>
      </div>

      <div></div>

      {(iconTags.length > 0 || regulations.length > 0) && (
        <div className="fsc-risk-item__tags-footer">
          {iconTags.length > 0 && (
            <div className="fsc-risk-item__tags">
              {iconTags.map((label, i) => (
                <span
                  key={i}
                  style={{ display: "flex" }}
                  title={formatTypeLabel(label)}
                >
                  <IconOrInitials
                    src={categoryIcons[label]}
                    label={formatTypeLabel(label)}
                    className="category"
                    size={20}
                  />
                </span>
              ))}
            </div>
          )}

          {regulations.length > 0 && (
            <div className="fsc-risk-item__reg-row">
              {visibleRegs.map((reg) => (
                <span key={reg} className="fsc-risk-item__reg-pill">
                  {reg}
                </span>
              ))}
              {extraRegsCount > 0 && (
                <span className="fsc-risk-item__reg-pill fsc-risk-item__reg-pill--more">
                  +{extraRegsCount}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      <div className="fsc-risk-item__actions">
        {applied ? (
          <span className="fsc-risk-item__applied">
            <i className="ti ti-circle-check-filled" />
            {applied} applied
          </span>
        ) : (
          getButtonTitle().map((a) => (
            <button
              key={a}
              className="fsc-btn fsc-btn--ghost"
              onClick={(e) => {
                e.stopPropagation();
                onAction(item, a, dispatch);
              }}
              disabled={!canUpdate}
              title={
                !canUpdate
                  ? "You don't have permission to perform this action"
                  : undefined
              }
            >
              {a}
            </button>
          ))
        )}
      </div>
    </div>
  );
}

const getVariantColor = {
  SUCCESSFUL: "green",
  UNSUCCESSFUL: "red",
  PENDING: "gray",
};

const getVariantValue = {
  SUCCESSFUL: "Successful",
  UNSUCCESSFUL: "Unsuccessful",
  PENDING: "Pending",
};

export function RiskLogTable({ auditTrail, loading, isUserRisk }) {
  const columns = [
    {
      field: "timestamp",
      headerName: "Date",
      flex: 1.4,
      renderCell: ({ value }) => <span>{renderDateTime(value)}</span>,
    },
    {
      field: "action",
      headerName: "Action",
      flex: isUserRisk ? 2.4 : 1.5,
      renderCell: ({ value }) => <Badge variant="green">{value}</Badge>,
    },
    {
      field: "actionTakenBy",
      headerName: "Action Taken By",
      flex: 1.5,
      renderCell: ({ value }) => <span>{value || "-"}</span>,
    },

    {
      field: "fileName",
      headerName: "File",
      flex: 1.4,
    },
    {
      field: "source",
      headerName: "Source",
      width: 140,
    },
    {
      field: "scope",
      headerName: "Scope",
      width: 120,
      renderCell: ({ value }) => (
        <span className="fsc-badge fsc-badge--purple">{value}</span>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: ({ value }) => (
        <Badge variant={getVariantColor[value]}>
          {isNull(value) ? "Pending" : getVariantValue[value]}
        </Badge>
      ),
    },
  ];

  return (
    <div
      style={{
        height: 450,
        width: "100%",
      }}
    >
      <DataGrid
        rows={auditTrail?.rows ?? []}
        columns={columns}
        loading={loading}
        pagination
        paginationMode="client"
        pageSizeOptions={[10, 20, 50]}
        initialState={{
          pagination: {
            paginationModel: {
              page: auditTrail?.page ?? 0,
              pageSize: auditTrail?.pageSize ?? 10,
            },
          },
        }}
        disableRowSelectionOnClick
        disableColumnMenu
        slots={{
          noRowsOverlay: () => (
            <GridOverlay>No audit history found</GridOverlay>
          ),
        }}
      />
    </div>
  );
}

export function RiskFullScreen({
  category,
  appliedActions,
  onAction,
  onBulkAction,
}) {
  const [viewMode, setViewMode] = useState("cards"); // "cards" | "table" | "clusters" | "logs"
  const [selectedIds, setSelectedIds] = useState(new Set());
  const permissions = useSelector((state) => state.applicationData.permissions);
  const canUpdate =
    Array.isArray(permissions) && permissions.includes("UPDATE_DATA_CONTROL");

  useEffect(() => {
    setViewMode("cards");
    setSelectedIds(new Set());
  }, [category.id]);

  const categoryCache = useSelector(
    (state) => state.dataControlReducer.dataControlFilesData[category.id],
  ) || { summary: {}, items: [], loadedCount: 0 };

  const { summary, items, loadedCount } = categoryCache;
  const totalCount = summary?.totalFlagged; // undefined until first fetch

  const fileAuditTrail = useSelector(
    (state) => state.dataControlReducer.fileAuditTrail,
  );
  const userAuditTrail = useSelector(
    (state) => state.dataControlReducer.userAuditTrail,
  );
  const domainAuditTrail = useSelector(
    (state) => state.dataControlReducer.domainAuditTrail,
  );
  const loader = useSelector((state) => state.dataControlReducer.loader);
  const userLocationAuditTrail = useSelector(
    (state) => state.dataControlReducer.userLocationAuditTrail,
  );

  const dispatch = useDispatch();

  const auditTrail =
    category.id === "user-risk"
      ? userAuditTrail
      : category.id === "org-wide"
        ? domainAuditTrail
        : category.id === "location"
          ? userLocationAuditTrail
          : fileAuditTrail;

  const isUserRisk = category.id === "user-risk";

  useEffect(() => {
    if (viewMode !== "clusters") return;
    const target =
      typeof totalCount === "number"
        ? Math.min(totalCount, CLUSTER_FETCH_SIZE)
        : CLUSTER_FETCH_SIZE;
    ensureCategoryLoaded(
      dispatch,
      category.id,
      target,
      loadedCount,
      totalCount,
    );
  }, [viewMode, category.id, loadedCount, totalCount, dispatch]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleTableSelectionChange = (model) => {
    setSelectedIds(new Set(model));
  };

  const selectedCount = selectedIds.size;

  const [confirmItems, setConfirmItems] = useState(null);

  const openBulkConfirm = () => {
    const visibleItems = items.slice(0, loadedCount);
    const targetItems =
      selectedCount > 0
        ? visibleItems.filter((f) => selectedIds.has(f.id))
        : visibleItems;

    setSelectedIds(new Set(targetItems.map((it) => it.id)));
    setConfirmItems(targetItems);
  };

  const cancelBulkConfirm = () => setConfirmItems(null);

  const confirmBulkRemove = () => {
    if (!confirmItems || confirmItems.length === 0) return;

    const groups = confirmItems.reduce((acc, item) => {
      const key = item.dataSourceId;
      if (!acc[key]) acc[key] = [];
      acc[key].push(item.fileId || item.alertId || item.id);
      return acc;
    }, {});

    Object.entries(groups).forEach(([dataSourceId, fileIds]) => {
      dispatch({
        type: REVOKE_FILE_ACCESS,
        payload: {
          fileIds,
          dataSourceId,
          openRiskId: category.id,
          size: Math.max(loadedCount, DECK_PAGE_SIZE),
        },
      });
    });

    setSelectedIds(new Set());
    setConfirmItems(null);
  };

  const badgeStats = [
    { label: "Total flagged", value: summary.totalFlagged || 0, tone: "blue" },
    {
      label: "Restrictions Applied",
      value: summary.appliedRestrictions || 0,
      tone: "green",
    },
    { label: "High severity", value: summary.highSeverity || 0, tone: "red" },
  ];

  const isLogsView = viewMode === "logs";

  return (
    <>
      {loader && (
        <img
          src={Page_Loader}
          alt="loading"
          className="loader-data-source data-control-loader"
        />
      )}

      <div className="fsc-fullscreen">
        <div className="fsc-fullscreen__intro">
          <span
            className={`fsc-fullscreen__icon fsc-fullscreen__icon--${category.severity}`}
          >
            <i className={`ti ${category.icon}`} />
          </span>

          <div>
            <p className="fsc-fullscreen__summary">{category.summary}</p>
          </div>
        </div>

        <div className="fsc-stat-badge-row">
          {badgeStats.map((s, i) => (
            <StatCard key={i} {...s} />
          ))}
        </div>

        <div className="fsc-fullscreen__section">
          <div className="fsc-fullscreen__section-head">
            <div className="fsc-fullscreen__section-head-left">
              <h2 className="fsc-section-label" style={{ margin: 0 }}>
                <i
                  className={`ti ${isLogsView ? "ti-history" : "ti-list-check"}`}
                />{" "}
                {isLogsView
                  ? "Action log"
                  : `Flagged ${category.subjectLabel.toLowerCase()}s — take action`}
              </h2>

              {!isLogsView && selectedCount > 0 && (
                <span className="fsc-selected-count">
                  Selected {selectedCount}
                </span>
              )}
            </div>

            <div className="fsc-fullscreen__section-head-right">
              {!isLogsView && (
                <button
                  className="fsc-btn fsc-btn--danger fsc-quick-action-btn"
                  onClick={openBulkConfirm}
                  disabled={!canUpdate || (summary.totalFlagged || 0) === 0}
                  title={
                    !canUpdate
                      ? "You don't have permission to perform this action"
                      : (summary.totalFlagged || 0) === 0
                        ? "No flagged items to act on"
                        : undefined
                  }
                >
                  <i className="ti ti-shield-check" />
                  {getBulkActionLabel(category.id)} for all
                  {selectedCount > 0 ? ` (${selectedCount})` : ""}
                </button>
              )}

              <div className="fsc-view-toggle">
                <button
                  className={`fsc-view-toggle__opt ${
                    viewMode === "cards" ? "fsc-view-toggle__opt--active" : ""
                  }`}
                  onClick={() => setViewMode("cards")}
                >
                  <i className="ti ti-layout-grid" /> Cards
                </button>
                <button
                  className={`fsc-view-toggle__opt ${
                    viewMode === "table" ? "fsc-view-toggle__opt--active" : ""
                  }`}
                  onClick={() => setViewMode("table")}
                >
                  <i className="ti ti-table" /> Table
                </button>
                <button
                  className={`fsc-view-toggle__opt ${
                    viewMode === "clusters"
                      ? "fsc-view-toggle__opt--active"
                      : ""
                  }`}
                  onClick={() => setViewMode("clusters")}
                >
                  <i className="ti ti-chart-bubble" /> Clusters
                </button>
                <button
                  className={`fsc-view-toggle__opt ${
                    viewMode === "logs" ? "fsc-view-toggle__opt--active" : ""
                  }`}
                  onClick={() => setViewMode("logs")}
                >
                  <i className="ti ti-history" /> Action Log
                </button>
              </div>
            </div>
          </div>

          {viewMode === "cards" ? (
            <RiskItemDeck
              categoryId={category.id}
              items={items}
              loadedCount={loadedCount}
              totalCount={totalCount}
              dispatch={dispatch}
              appliedActions={appliedActions}
              onAction={onAction}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              loading={loader}
              canUpdate={canUpdate}
            />
          ) : viewMode === "table" ? (
            <RiskItemsTable
              categoryId={category.id}
              items={items}
              loadedCount={loadedCount}
              totalCount={totalCount}
              dispatch={dispatch}
              appliedActions={appliedActions}
              onAction={onAction}
              loading={loader}
              selectedIds={selectedIds}
              onSelectionModelChange={handleTableSelectionChange}
              isUserRisk={isUserRisk}
              canUpdate={canUpdate}
            />
          ) : viewMode === "clusters" ? (
            <DataControlSensitivityScatter
              items={items.slice(0, loadedCount)}
              categoryId={category.id}
              appliedActions={appliedActions}
              onAction={onAction}
              dispatch={dispatch}
            />
          ) : (!auditTrail?.rows || auditTrail.rows.length === 0) && !loader ? (
            <p className="fsc-fullscreen__no-log">
              No audit history found for this category.
            </p>
          ) : (
            <RiskLogTable
              auditTrail={auditTrail}
              loading={loader}
              isUserRisk={isUserRisk}
            />
          )}
        </div>
      </div>

      {confirmItems && (
        <div className="fsc-modal-overlay" onClick={cancelBulkConfirm}>
          <div className="fsc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fsc-modal__header">
              <span className="fsc-modal__icon">
                <i className="ti ti-shield-check" />
              </span>
              <p className="fsc-modal__title">
                {getBulkActionLabel(category.id)} for {confirmItems.length}{" "}
                {confirmItems.length === 1
                  ? category.subjectLabel.toLowerCase()
                  : `${category.subjectLabel.toLowerCase()}s`}
                ?
              </p>
            </div>

            <p className="fsc-modal__message">
              This will apply {getBulkActionLabel(category.id).toLowerCase()} to{" "}
              {confirmItems.length} selected{" "}
              {confirmItems.length === 1 ? "item" : "items"}. This action can't
              be undone from here.
            </p>

            <div className="fsc-modal__actions">
              <button
                className="fsc-btn fsc-btn--outline"
                onClick={cancelBulkConfirm}
              >
                Cancel
              </button>
              <button
                className="fsc-btn fsc-btn--danger"
                onClick={confirmBulkRemove}
                disabled={!canUpdate}
                title={
                  !canUpdate
                    ? "You don't have permission to perform this action"
                    : undefined
                }
              >
                <i className="ti ti-shield-check" />
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
